import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UptimeWingLogo } from "@/components/shared/UptimeWingLogo";
import { BrandName } from "@/components/shared/BrandName";
import { useAuth } from "@/hooks/useAuth";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

interface LandingHeaderProps {
  currentPath?: string;
}

export function LandingHeader({ currentPath = "/" }: LandingHeaderProps) {
  const { user, signInWithGoogle, loading } = useAuth();
  const [authLoading, setAuthLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleGoogleLogin = async () => {
    setAuthLoading(true);
    localStorage.setItem("just_clicked_login", "true");
    try {
      await signInWithGoogle();
    } catch {
      setAuthLoading(false);
      localStorage.removeItem("just_clicked_login");
    }
  };

  const handleScrollOrNavigate = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(`/#${targetId}`);
    }
  };

  return (
    <header className="sticky top-0 border-b-2 border-black w-full bg-white z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        
        {/* Logo and Brand */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <Link to="/" className="hover:opacity-80 transition-all flex items-center gap-2">
            <UptimeWingLogo className="h-8 w-8 text-black" />
            <BrandName size="sm" />
          </Link>
        </div>

        {/* Center Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 text-sm font-black uppercase tracking-tight text-black">
          <a 
            href="#mechanism" 
            onClick={(e) => handleScrollOrNavigate(e, "mechanism")} 
            className="hover:underline transition-all"
          >
            PRODUCT
          </a>
          <Link to="/pricing" className="hover:underline transition-all">
            PRICING
          </Link>
          <Link to="/tools" className="hover:underline transition-all">
            FREE TOOLS
          </Link>
          <Link to="/docs" className="hover:underline transition-all">
            DOCS
          </Link>
        </nav>

        {/* Right Desktop CTA / Auth */}
        <div className="hidden md:flex items-center gap-6">
          {user ? (
            <Link to="/dashboard">
              <Button className="h-10 rounded-none border-2 border-black bg-black text-white hover:bg-white hover:text-black font-black uppercase text-xs tracking-wider transition-all">
                DASHBOARD
              </Button>
            </Link>
          ) : (
            <Button
              onClick={handleGoogleLogin}
              disabled={authLoading}
              className="h-10 rounded-none border-2 border-black bg-black text-white hover:bg-white hover:text-black font-black uppercase text-xs tracking-wider transition-all"
            >
              GET STARTED
            </Button>
          )}
        </div>

        {/* Mobile menu trigger */}
        <div className="flex items-center md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 border-2 border-black bg-white hover:bg-zinc-50 rounded-none shadow-[2px_2px_0_0_#000] transition-all flex items-center justify-center"
              >
                <Menu className="h-5 w-5 text-black" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[300px] border-l-4 border-black bg-white p-6 flex flex-col justify-between"
            >
              <div className="space-y-8 mt-8">
                {/* Header inside mobile menu */}
                <div className="flex items-center gap-2">
                  <UptimeWingLogo className="h-8 w-8 text-black" />
                  <BrandName size="sm" />
                </div>

                {/* Mobile Navigation Links */}
                <div className="flex flex-col gap-4 font-mono text-sm font-black uppercase">
                  <a
                    href="#mechanism"
                    onClick={(e) => {
                      setOpen(false);
                      handleScrollOrNavigate(e, "mechanism");
                    }}
                    className="py-2 border-b border-zinc-150 hover:underline"
                  >
                    PRODUCT
                  </a>
                  <Link
                    to="/pricing"
                    onClick={() => setOpen(false)}
                    className="py-2 border-b border-zinc-150 hover:underline"
                  >
                    PRICING
                  </Link>
                  <Link
                    to="/tools"
                    onClick={() => setOpen(false)}
                    className="py-2 border-b border-zinc-150 hover:underline"
                  >
                    FREE TOOLS
                  </Link>
                  <Link
                    to="/docs"
                    onClick={() => setOpen(false)}
                    className="py-2 border-b border-zinc-150 hover:underline"
                  >
                    DOCS
                  </Link>
                </div>
              </div>

              {/* Mobile CTA */}
              <div className="pb-8">
                {user ? (
                  <Link to="/dashboard" onClick={() => setOpen(false)}>
                    <Button className="w-full h-12 rounded-none border-2 border-black bg-black text-white hover:bg-white hover:text-black font-black uppercase text-xs tracking-wider transition-all">
                      DASHBOARD
                    </Button>
                  </Link>
                ) : (
                  <Button
                    onClick={() => {
                      setOpen(false);
                      handleGoogleLogin();
                    }}
                    disabled={authLoading}
                    className="w-full h-12 rounded-none border-2 border-black bg-black text-white hover:bg-white hover:text-black font-black uppercase text-xs tracking-wider transition-all"
                  >
                    START MONITORING
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>

      </div>
    </header>
  );
}
